import { useState, useMemo, useEffect, useRef } from 'react'
import BackButton from '../components/ui-components/BackButton'
import { useSearchParams } from 'react-router-dom'
import { goBack, onReady, openInBrowser, openVideoPlayer, trackEvent, updateAppStatusBar } from '../utils/appBridge'
import { EventNames } from '../types/misc.types'
import RenderHTML from '../components/ui-components/RenderHTML'
import MFAccordion from '../components/ui-components/MFAccordion'
import Stopwatch from '../components/Questionnaire/stopwatch/Stopwatch'
import { useQuery } from '@tanstack/react-query'
import { smartRebalance } from '../repository/rebalance'
import { StatusResponse } from '../types/rebalance'
import { motion } from 'framer-motion'
import { EliteLandingPageRemark } from '../components/elite/EliteLandingPageRemark'
import { PowerRebalancingEliteButton } from '../components/power-rebalancing/PowerRebalancingEliteButton'
import Button from '../components/ui-components/Button'
import { getBottomPadding, getTopPadding } from '../utils/sessionStorage'
import { rebalanceCostBannerData } from '../data/rebalanceData'

const rebalanceAccordions = [
  {
    icon: '/maximizing-returns.svg',
    title: 'Maximising returns',
    caption: 'in various market conditions',
    content: `We analyse your existing funds and recommend strong performers within the same category—so your portfolio keeps up with market movements, without changing your risk profile.`
  },
  {
    icon: '/tax-optimisations.svg',
    title: 'Tax optimisations',
    caption: 'to minimise STCG tax implication',
    content: `Our recommendations factor in tax impact (both LTCG and STCG), so you don't lose gains to unnecessary taxes.`
  },
  {
    icon: '/quarterly-plan.svg',
    title: 'Reviewed every month',
    caption: 'Rebalancing suggested only if needed',
    content: `We review your portfolio every month to keep it aligned with your goals. If your portfolio is already in great shape, no changes will be suggested.`
  }
]

const SmartRebalanceIcon = `
<svg xmlns="http://www.w3.org/2000/svg" width="173" height="20" fill="none" viewBox="0 0 173 20"><path fill="#992e00" d="M8.168 13.457c.287-.542.98-.746 1.55-.457.568.29.796.962.509 1.504l-.79 1.492c.818.064 2.954.545 4.95 1.953.493.387.574 1.094.183 1.58a1.15 1.15 0 0 1-1.602.18c-.33-.351-1.585-1.172-3.972-1.634-.394-.05-.76-.014-1.11 0l-.142-.014q-.024.002-.05.003l-3.75.14c-.635.023-1.182-.456-1.22-1.07-.04-.613.443-1.129 1.078-1.153l3.091-.115zM163.417 13c.561-.29 1.244-.085 1.527.457l1.259 2.41 3.046.114a1.09 1.09 0 0 1 1.065 1.154 1.16 1.16 0 0 1-1.204 1.068l-3.699-.14q-.024 0-.048-.002l-.14.014c-.346-.014-.706-.05-1.095 0-2.354.463-3.592 1.283-3.918 1.635a1.124 1.124 0 1 1-1.399-1.76c1.969-1.41 4.075-1.89 4.882-1.954l-.779-1.492c-.283-.542-.057-1.215.503-1.504"/><path fill="#992e00" fill-rule="evenodd" d="M151.148 6.308c1.024 0 1.952.431 2.416 1.055v-.927h1.728v7.616c0 2.08-1.584 3.488-3.904 3.488-1.888 0-3.375-1.008-3.632-2.496l1.632-.256c.096.8.944 1.327 2 1.327 1.296 0 2.193-.767 2.193-1.887v-.976c-.4.608-1.345 1.023-2.401 1.023-2.4 0-3.776-1.647-3.776-3.983s1.376-3.984 3.744-3.984m.272 1.52c-1.376 0-2.272.976-2.272 2.464s.897 2.464 2.272 2.464c1.36 0 2.256-.976 2.256-2.464s-.896-2.465-2.256-2.465" clip-rule="evenodd"/><path fill="#992e00" d="M6.234 11.448a1.147 1.147 0 0 1 1.558.403c.316.537.131 1.225-.414 1.539l-2.253 1.297a1.16 1.16 0 0 1-1.002.072L1.416 13.68a1.116 1.116 0 0 1-.626-1.464 1.15 1.15 0 0 1 1.485-.622l2.19.872zM165.315 11.852c.312-.537 1-.717 1.537-.404l1.745 1.019 2.159-.872a1.13 1.13 0 0 1 1.465.622 1.12 1.12 0 0 1-.617 1.464l-2.67 1.078c-.322.13-.687.103-.988-.072l-2.223-1.297a1.126 1.126 0 0 1-.408-1.539"/><path fill="#992e00" fill-rule="evenodd" d="M29.97 6.308c2.416 0 4.16 1.744 4.16 4.16s-1.744 4.16-4.16 4.16c-2.432 0-4.16-1.744-4.16-4.16s1.728-4.16 4.16-4.16m0 1.52c-1.44 0-2.4 1.088-2.4 2.64s.96 2.64 2.4 2.64c1.424 0 2.4-1.088 2.4-2.64s-.976-2.64-2.4-2.64M51.168 6.308c2.592 0 3.968 1.888 3.968 3.856 0 .24-.016.576-.032.72h-6.16c.16 1.391 1.056 2.255 2.336 2.256 1.056 0 1.808-.496 2.016-1.376l1.648.416c-.432 1.552-1.808 2.448-3.696 2.448-2.56 0-4.016-1.984-4.016-4.16s1.376-4.16 3.936-4.16m0 1.376c-1.104 0-1.888.672-2.144 1.872h4.256c-.112-1.088-.832-1.872-2.112-1.872M80.247 6.308c2.591 0 3.967 1.888 3.967 3.856 0 .24-.016.576-.032.72h-6.16c.16 1.391 1.056 2.255 2.336 2.256 1.056 0 1.808-.496 2.016-1.376l1.648.416c-.432 1.552-1.808 2.448-3.695 2.448-2.56 0-4.017-1.984-4.017-4.16s1.377-4.16 3.937-4.16m0 1.376c-1.104 0-1.889.672-2.145 1.872h4.256c-.112-1.088-.832-1.872-2.111-1.872M87.596 7.444c.48-.672 1.424-1.136 2.464-1.136 2.432 0 3.824 1.728 3.824 4.16s-1.391 4.16-3.855 4.16c-1.072 0-2.049-.48-2.433-1.152V14.5h-1.744V3.172h1.744zm2.208.384c-1.408 0-2.336 1.04-2.336 2.64 0 1.584.928 2.64 2.336 2.64s2.336-1.056 2.336-2.64c0-1.6-.928-2.64-2.336-2.64M98.518 6.308c2.048 0 3.456 1.152 3.456 3.168V14.5h-1.713v-1.232c-.464.815-1.455 1.36-2.527 1.36-1.6 0-2.704-1.009-2.704-2.385 0-1.584 1.232-2.431 3.712-2.687l1.519-.16v-.113c0-.991-.687-1.6-1.743-1.6-.976 0-1.664.577-1.776 1.537l-1.569-.288c.256-1.584 1.633-2.624 3.345-2.624m.208 4.56c-1.312.143-1.984.624-1.984 1.327 0 .64.527 1.088 1.343 1.088 1.36 0 2.16-.944 2.176-2.256v-.336zM110.909 6.308c2.047 0 3.456 1.152 3.456 3.168V14.5h-1.713v-1.232c-.464.815-1.456 1.36-2.527 1.36-1.6 0-2.705-1.009-2.705-2.385.001-1.584 1.233-2.431 3.712-2.687l1.52-.16v-.113c0-.991-.688-1.6-1.743-1.6-.976 0-1.665.577-1.777 1.537l-1.568-.288c.256-1.584 1.633-2.624 3.345-2.624m.208 4.56c-1.312.143-1.984.624-1.985 1.327 0 .64.528 1.088 1.344 1.088 1.36 0 2.16-.944 2.176-2.256v-.336z" clip-rule="evenodd"/><path fill="#992e00" d="M129.389 6.308c1.984 0 3.409 1.055 3.681 2.72l-1.696.335c-.161-.911-.912-1.536-1.936-1.536-1.392 0-2.288 1.04-2.288 2.64s.912 2.64 2.288 2.64c1.024 0 1.744-.56 1.936-1.503l1.711.335c-.336 1.68-1.728 2.689-3.696 2.689-2.416 0-4.048-1.68-4.048-4.16s1.632-4.16 4.048-4.16"/><path fill="#992e00" fill-rule="evenodd" d="M21.52 3.3c2.224 0 3.664 1.312 3.664 3.344s-1.44 3.344-3.664 3.344h-2.528V14.5h-1.888V3.3zm-2.528 5.055h2.4c1.184 0 1.888-.64 1.888-1.711s-.704-1.712-1.888-1.712h-2.4z" clip-rule="evenodd"/><path fill="#992e00" d="m38.132 12.067 1.745-5.631h1.616l1.728 5.631 1.52-5.631h1.776l-2.48 8.064H42.42l-1.743-5.616-1.744 5.616h-1.6l-2.497-8.064h1.777zM60.966 6.308c.304 0 .656.047.896.143l-.096 1.68c-.288-.127-.672-.192-1.04-.192-1.184 0-2.224 1.057-2.224 3.552V14.5h-1.744V6.436h1.744v1.536c.432-.96 1.488-1.664 2.464-1.664"/><path fill="#992e00" fill-rule="evenodd" d="M71.234 3.3c2.16 0 3.567 1.264 3.567 3.216 0 1.312-.703 2.4-1.887 2.912l3.072 5.072h-2.193l-2.8-4.752h-2.08V14.5h-1.871l-.016-11.2zm-2.32 4.8h2.047c1.232 0 1.937-.576 1.937-1.584 0-.992-.705-1.584-1.937-1.584h-2.047z" clip-rule="evenodd"/><path fill="#992e00" d="M105.768 14.5h-1.744V3.172h1.744zM120.814 6.308c1.712 0 2.912 1.136 2.912 2.88V14.5h-1.744V9.588c0-1.008-.624-1.712-1.648-1.712-1.36 0-2.175 1.152-2.175 3.088V14.5h-1.744V6.436h1.744v1.343c.544-.863 1.471-1.471 2.655-1.471M136.409 14.5h-1.744V6.436h1.744zM142.876 6.308c1.712 0 2.913 1.136 2.913 2.88V14.5h-1.745V9.588c0-1.008-.623-1.712-1.647-1.712-1.36 0-2.176 1.152-2.176 3.088V14.5h-1.744V6.436h1.744v1.343c.544-.863 1.472-1.471 2.655-1.471M1.195 7.144a1.16 1.16 0 0 1 1.61-.132l1.649 1.385 1.99-.899a1.14 1.14 0 0 1 1.51.549 1.126 1.126 0 0 1-.578 1.49l-2.656 1.2c-.405.182-.878.119-1.214-.163L1.31 8.726a1.107 1.107 0 0 1-.114-1.582M170.233 7.012c.47-.4 1.181-.342 1.589.132a1.12 1.12 0 0 1-.113 1.582l-2.167 1.848c-.33.282-.798.345-1.198.162l-2.619-1.2a1.13 1.13 0 0 1-.568-1.49 1.116 1.116 0 0 1 1.487-.548l1.964.9zM2.973 3.18a1.15 1.15 0 0 1 1.612.024l1.09 1.12 1.83-.5a1.13 1.13 0 0 1 1.396.781 1.136 1.136 0 0 1-.81 1.384l-2.486.68a1.14 1.14 0 0 1-1.12-.3L2.93 4.77a1.113 1.113 0 0 1 .043-1.588M168.478 3.204a1.124 1.124 0 0 1 1.59-.023c.45.432.469 1.143.042 1.589l-1.533 1.6c-.283.294-.707.409-1.105.299l-2.451-.68a1.14 1.14 0 0 1-.799-1.384c.16-.598.776-.948 1.376-.782l1.806.501zM135.529 2.836c.656 0 1.151.496 1.151 1.136 0 .624-.495 1.12-1.151 1.12-.64 0-1.12-.496-1.12-1.12 0-.64.48-1.136 1.12-1.136M7.113.66a1.15 1.15 0 0 1 1.57-.355c.533.33.695 1.023.361 1.55l-.487.768a1.15 1.15 0 0 1-1.571.355 1.115 1.115 0 0 1-.36-1.55zM164.436.305c.526-.33 1.22-.171 1.55.355l.48.769a1.124 1.124 0 1 1-1.905 1.194l-.48-.769a1.124 1.124 0 0 1 .355-1.55"/></svg>
`

export const PowerRebalancePage = () => {
  const onReadyLock = useRef<boolean>(false)
  const backButtonRef = useRef<HTMLButtonElement>(null)

  const [searchParams] = useSearchParams()
  const videoId = '2ddf282438344ae2b80bae383bb44220'
  const moduleId = 'MD2943832c786c493ea40e4ed1d82f4abf'
  const courseId = 'COURSEde919cce87264b1480e91195a96442b0'
  const videoUrl = `https://pumobileweb.uni.club/power-academy/video-player?videoId=${videoId}&moduleId=${moduleId}&courseId=${courseId}&prevStatusBarColor=20A5DE`
  const eliteMemberShipStatus = useMemo(() => {
    return searchParams?.get('eliteMemberShipStatus') ?? 'REGULAR'
  }, [searchParams])

  const isUserElite = eliteMemberShipStatus === 'ELITE'

  const filteredBannerData = useMemo(() => {
    return isUserElite ? rebalanceCostBannerData.slice(0, 1) : rebalanceCostBannerData
  }, [isUserElite])

  // Add the useQuery hook for smartRebalance
  const {
    data: queryData,
    isLoading,
    isError
  } = useQuery({
    queryKey: ['smartRebalance'],
    queryFn: smartRebalance,
    retry: 1
  })

  // Safely access nested data
  const rebalanceData: StatusResponse | undefined = queryData?.data

  const handleBack = () => {
    goBack()
  }
  const handlePageReady = () => {
    if (!onReadyLock.current) {
      onReadyLock.current = true
      onReady()
    }
  }

  useEffect(() => {
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: {
        screenType: 'MF_Smart_Rebalancing'
      }
    })
    setTimeout(() => {
      handlePageReady()
    }, 150)
    updateAppStatusBar('FFFFFF', 'dark')
  }, [])

  // Power Rebalancing_B01: initial TalkBack focus should land on Back, not mid-page headings
  useEffect(() => {
    if (isLoading) return
    const id = window.setTimeout(() => {
      backButtonRef.current?.focus()
    }, 0)
    return () => window.clearTimeout(id)
  }, [isLoading, rebalanceData?.userEligibleForRebalance])

  const onBannerClick = () => {
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        screenType: 'MF_Smart_Rebalancing',
        eventId: 'Banner'
      }
    })
    const blogUrl = `https://www.powerup.money/blog/newsletter/we-analysed-50000-portfolios-and-78-need-rebalancing`
    openInBrowser(eliteMemberShipStatus === 'ELITE' ? blogUrl : blogUrl)
  }

  if (isLoading) {
    return (
      <div className='p-10'>
        <div className='flex flex-col gap-4'>
          <div className='h-[9px] max-w-[72px] bg-grey80 animate-pulse rounded'></div>
          <div className='w-[200px] h-[15px] bg-grey80 animate-pulse rounded'></div>
        </div>
      </div>
    )
  } else if (rebalanceData?.userEligibleForRebalance) {
    return (
      <div key={'rebalance-data'} className={`h-screen flex flex-col relative w-full`}>
        <div
          style={{ paddingTop: `calc(${getTopPadding()}px + 12px)` }}
          className='flex w-max px-5 items-center justify-start pb-6 bg-[#FFF4EB] !w-full'
        >
          {/* h-11 w-11: VoiceOver/TalkBack hit target for Back (PowerRebalancing_B03) */}
          <div className='flex h-11 w-11 shrink-0 items-center justify-center'>
            <BackButton ref={backButtonRef} fill={'#000000'} size={24} onClick={handleBack} aria-label='Back' />
          </div>
        </div>
        <div className='flex flex-1 h-1 w-full'>
          <div className={`h-full no-scrollbar overflow-y-auto overflow-x-hidden overscroll-none w-full flex flex-col`}>
            <SmartRebalanceLogo className='pb-4 px-5 bg-[#FFF4EB]' />
            <div>
              <Stopwatch
                isRebalanceAvailable={rebalanceData?.rebalanceAvailable}
                isUserElite={isUserElite}
                rebalanceData={rebalanceData}
                isLoading={isLoading}
                isError={isError}
              />
              <div className='bg-[#FFF4EB] py-6 px-5'>
                <div className='bg-[#FFE8D0E5] text-orange10 flex flex-col text-sm font-medium rounded-xl justify-between'>
                  {filteredBannerData.map((banner, index) => (
                    <div key={index}>
                      <button
                        type='button'
                        onClick={banner.onClick}
                        aria-label={banner.text}
                        className='w-full bg-transparent border-none p-0 text-left cursor-pointer'
                      >
                        <div className='inline-flex items-center w-full justify-between p-3'>
                          <div className='inline-flex items-center gap-2'>
                            <span aria-hidden='true'>
                              <RenderHTML innerHTMLText={banner.leftIcon} />
                            </span>
                            <span>{banner.text}</span>
                          </div>
                          <span aria-hidden='true'>
                            <RenderHTML innerHTMLText={banner.rightIcon} />
                          </span>
                        </div>
                      </button>
                      {index < filteredBannerData.length - 1 && <div className='w-full border-b border-white' />}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className={`flex flex-col h-max w-full relative z-0 px-5 mb-6 flex-grow mt-10`}>
              <h2 className='text-[#232323] leading-[130%] text-[22px] font-[500] pb-4'>
                How does rebalancing help?
              </h2>
              <button
                type='button'
                onClick={() => {
                  trackEvent({
                    eventName: EventNames.ButtonTap,
                    payload: {
                      screenType: 'MF_Smart_Rebalancing',
                      buttonLabel: 'Open Video Player',
                      eventId: 'rebalancing_video'
                    }
                  })
                  openVideoPlayer(videoUrl)
                }}
                aria-label='Know what you get in your Rebalancing plan, by Arush Kapoor, Head of Product'
                className='relative mb-12 border-none bg-transparent p-0 cursor-pointer w-full text-left'
              >
                <svg
                  aria-hidden='true'
                  className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'
                  xmlns='http://www.w3.org/2000/svg'
                  width='40'
                  height='40'
                  viewBox='0 0 40 40'
                  fill='none'
                >
                  <circle cx='20' cy='20' r='20' fill='#D6DCE1' />
                  <path
                    d='M17 26.9991V14.0723C17 13.2353 17.967 12.7686 18.6222 13.2895L26.9866 19.9381C27.4992 20.3456 27.4884 21.1276 26.9647 21.5207L18.6003 27.7988C17.9411 28.2936 17 27.8233 17 26.9991Z'
                    fill='white'
                  />
                </svg>
                <img className='rounded-xl' src='/images/rebalancing-plan.png' alt='' />
              </button>
              <h2 className='text-[#232323] leading-[130%] text-[22px] font-[500] pb-4'>
                Keep your portfolio in top form
              </h2>
              <div className='text-[#6A6E6E] font-[400] leading-[150%] text-[14px] pb-6'>
                Every month, we assess your portfolio to see if any smart switches can be made. Here's what we focus
                on:
              </div>
              <div>
                <SmartRebalanceAccordions />
              </div>
              <a
                href='https://www.powerup.money/blog/newsletter/we-analysed-50000-portfolios-and-78-need-rebalancing'
                style={{ marginBottom: `calc(${getBottomPadding()}px + 10px)` }}
                onClick={(e) => {
                  e.preventDefault()
                  onBannerClick()
                }}
                aria-label='Know why rebalancing matters for you. We analysed 11,00,000+ portfolios and 78% need rebalancing. Read more here.'
                className='relative w-full max-w-lg rounded-xl mt-10 block'
              >
                <img
                  src='https://cdn.powerup.money/assets/images/promocard_web.png'
                  alt=''
                  aria-hidden='true'
                  className='w-full h-auto object-contain'
                />
              </a>
            </div>
          </div>
        </div>
      </div>
    )
  } else {
    return (
      <div key={'rebalance-data-non-eligible'} className={`h-screen flex flex-col relative w-full`}>
        <div
          style={{ paddingTop: `calc(${getTopPadding()}px + 12px)` }}
          className={`flex w-max px-5 items-center justify-center 
          rounded-[50%] animate-fade-150 pb-6`}
        >
          {/* h-11 w-11: VoiceOver/TalkBack hit target for Back (PowerRebalancing_B03) */}
          <div className='flex h-11 w-11 shrink-0 items-center justify-center'>
            <BackButton ref={backButtonRef} fill={'#000000'} size={24} onClick={handleBack} aria-label='Back' />
          </div>
        </div>
        <div className='flex flex-1 h-1 w-full '>
          <div className='h-full no-scrollbar overflow-y-auto overflow-x-hidden overscroll-none w-full px-5'>
            <div className='flex flex-col h-max w-full relative z-0 pb-48'>
              <SmartRebalanceLogo className='pb-4' />
              <h2 className='text-[#232323] leading-[130%] text-[22px] font-[500] pb-4'>
                Keep your portfolio in top form
              </h2>
              <div className='text-[#6A6E6E] font-[400] leading-[150%] text-[14px] pb-6'>
                Every month, we assess your portfolio to see if any smart switches can be made. Here’s what we focus
                on:
              </div>
              <div>
                <SmartRebalanceAccordions />
              </div>
              <div className='pt-8 w-full'>
                <EliteLandingPageRemark isElite>
                  <div className='font-[500] w-[90%]'>
                    {' '}
                    {eliteMemberShipStatus === 'ELITE' ? (
                      'You will receive your first rebalancing plan soon.'
                    ) : (
                      <div>
                        Available for Elite members only. <span className='font-[700]'> Join today </span> and secure
                        your <span className='font-[700]'> 1st rebalancing plan between 15-22nd Sep.</span>
                      </div>
                    )}
                  </div>
                </EliteLandingPageRemark>
              </div>

              <a
                href='https://www.powerup.money/blog/newsletter/we-analysed-50000-portfolios-and-78-need-rebalancing'
                style={{ marginBottom: `calc(${getBottomPadding()}px + 10px)` }}
                onClick={(e) => {
                  e.preventDefault()
                  onBannerClick()
                }}
                aria-label='Know why rebalancing matters for you'
                className='relative w-full max-w-lg rounded-xl mt-10 block'
              >
                <img
                  src='https://cdn.powerup.money/assets/images/promocard_web.png'
                  alt=''
                  aria-hidden='true'
                  className='w-full h-auto object-contain'
                />
              </a>
            </div>
          </div>
        </div>

        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          transition={{ type: 'tween', stiffness: 300, damping: 25, duration: 0.25 }}
          style={{ marginBottom: `calc(${getBottomPadding()}px + 10px)` }}
          className={`z-10 px-5 pt-5 pb-10 animate-fade-150 flex absolute w-full bottom-0 bg-gradient-to-b from-[rgba(255,255,255,0)] via-[rgba(255,255,255,0.3)] to-[rgba(255,255,255,0.8)]`}
        >
          {eliteMemberShipStatus === 'ELITE' ? (
            <Button isFullWidth buttonClassName='!h-[56px] !bg-[#232323] !text-white !text-[16px]' onClick={goBack}>
              {' '}
              Understood{' '}
            </Button>
          ) : (
            <PowerRebalancingEliteButton theme='INVERTED_ELITE' />
          )}
        </motion.div>
      </div>
    )
  }
}

export function SmartRebalanceLogo({ className }: { className?: string }) {
  return (
    <div className={className} role='img'>
      {/*
        PowerRebalancing_B03: VoiceOver skips role=img with only aria-hidden SVG children.
        sr-only text supplies the accessible name → "Power Rebalancing, image".
      */}
      <span className='sr-only'>Power Rebalancing</span>
      <div aria-hidden='true'>
        <RenderHTML innerHTMLText={SmartRebalanceIcon} />
      </div>
    </div>
  )
}

export const SmartRebalanceAccordions = () => {
  const [openAccordions, setOpenAccordions] = useState<Record<string, boolean>>(
    rebalanceAccordions?.reduce(
      (acc: Record<string, boolean>, accordion) => {
        acc[accordion.title] = false
        return acc
      },
      {} as Record<string, boolean>
    )
  )

  return (
    <div className='flex flex-col rounded-[12px] border border-[#F0F3F5] overflow-hidden'>
      {rebalanceAccordions?.map(({ title, icon, caption, content }, idx) => {
        return (
          <MFAccordion
            key={title}
            title={title}
            accessibleName={`${title}, ${caption}`}
            isExpanded={openAccordions?.[title]}
            icon={`${'/images/elite/smart-rebalance'}/${icon}`}
            iconClassName='!h-6 !w-6 !mt-2'
            accordionClassName='border-b border-[#F0F3F5]'
            middleContent={
              <div className='flex flex-col'>
                <div className='text-[#232323] text-[14px] font-[500]'>{title}</div>
                <div className='text-[#6A6E6E] text-[13px] font-[400]'>{caption}</div>
              </div>
            }
            onAccordionClick={() => {
              setOpenAccordions((accordionsPrev) => {
                return { ...accordionsPrev, [title]: !accordionsPrev?.[title] }
              })
            }}
          >
            <div className='text-[#6A6E6E] text-[13px] pb-6'>
              <RenderHTML innerHTMLText={content} />
            </div>
          </MFAccordion>
        )
      })}
    </div>
  )
}
